/**
 * still-quick-add.js
 *
 * +Cart popup — opens a modal with a tab per finish (Normal / Holo / Reverse
 * Holo) and a row per variant (Default Title in our store == one row), with
 * stock count and an Add button.
 *
 * Architecture (rewrite — replaces the inline IIFE that was in
 * still-quick-add.liquid):
 *
 *   • Single document-level click listener (event delegation). Works for
 *     statically-rendered AND AJAX-replaced cards equally — no re-init
 *     needed after facet toggles or sort changes.
 *   • Sibling lookup is done at click time via [data-group-key="..."] DOM
 *     query, NOT pre-cached on a "primary" card via data-group-variants.
 *     This is robust against the grouping JS running after the user clicks,
 *     or against the chosen primary being different from what you clicked.
 *   • Modal + backdrop are created lazily on first open, not at module load.
 *   • Escape closes. Click backdrop closes.
 *   • Public API stubs (`window.stillQaOpenModal`, `window.stillCardClick`)
 *     are exposed for backwards compatibility with any inline onclick that
 *     might still reference them.
 */

(function () {
  'use strict';

  let modal = null;
  let backdrop = null;
  let escapeBound = false;

  // ---------------------------------------------------------------------------
  // Modal lifecycle
  // ---------------------------------------------------------------------------

  function ensureModal() {
    if (modal && backdrop && modal.isConnected && backdrop.isConnected) return;

    backdrop = document.createElement('div');
    backdrop.className = 'still-qa-modal-backdrop';
    document.body.appendChild(backdrop);

    modal = document.createElement('div');
    modal.className = 'still-qa-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    document.body.appendChild(modal);

    backdrop.addEventListener('click', closeModal);

    if (!escapeBound) {
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeModal();
      });
      escapeBound = true;
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('active');
    if (backdrop) backdrop.classList.remove('active');
    document.body.style.overflow = '';
  }

  // ---------------------------------------------------------------------------
  // DOM helpers — read everything off the rendered card at click time
  // ---------------------------------------------------------------------------

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function fmtPrice(cents) {
    const n = Number(cents) || 0;
    return (n / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
    });
  }

  function findCard(node) {
    if (!(node instanceof Element)) return null;
    return node.closest('[data-product-handle]');
  }

  function parseVariants(card) {
    if (!card) return [];
    try {
      const raw = card.dataset.variantsJson || '[]';
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Return every card in the SAME view container (grid OR list — never
   * both) that shares the clicked card's set+number group_key.
   *
   * The collection page renders every product twice: once as a card
   * inside `#product-grid`, once as a row inside `#product-list`
   * (whichever is not the active view is hidden with `[hidden]`). A
   * document-wide `querySelectorAll` would double-count every sibling
   * — which is exactly what was causing Pinsir to show four tabs in
   * the +Cart modal (2 variants × 2 render paths) when Shopify actually
   * only has two Pinsir products.
   *
   * Falls back to [card] alone if the group_key isn't usable.
   */
  function findSiblings(card) {
    if (!card) return [];
    const groupKey = card.dataset.groupKey;
    if (!groupKey || groupKey === '-' || groupKey === 'undefined-' || groupKey.endsWith('-')) {
      return [card];
    }
    // Scope to the grid-or-list container the clicked card lives in.
    // That container exists on collection + search pages; elsewhere we
    // just use the document and hope for the best (no double-render).
    const scope = card.closest('#product-grid, #product-list') || document;
    const escaped = (window.CSS && CSS.escape) ? CSS.escape(groupKey) : groupKey;
    const matches = scope.querySelectorAll('[data-group-key="' + escaped + '"]');
    return matches.length ? Array.from(matches) : [card];
  }

  /**
   * Derive a finish label from a Shopify product handle.
   * `dri-005-reverse-holo` → "Reverse Holo"
   * `pfl-015-normal`       → "Normal"
   */
  function finishFromHandle(handle) {
    if (!handle) return 'Normal';
    const parts = handle.split('-');
    if (parts.length < 3) return 'Normal';
    const tail = parts.slice(2).join('-').toLowerCase();
    if (!tail || tail === 'normal') return 'Normal';
    if (tail === 'holo' || tail === 'holofoil') return 'Holofoil';
    if (tail === 'reverse-holo' || tail === 'reverse-holofoil') return 'Reverse Holo';
    // Fallback: title-case the slug. e.g. "promo" → "Promo".
    return tail.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function getCardMeta(card) {
    const titleEl = card.querySelector('.still-card__title, .still-list-row__title');
    const setEl = card.querySelector('.still-card__set, .still-list-row__set');
    const numEl = card.querySelector('.still-card__number, .still-list-row__number');
    const imgEl = card.querySelector('.still-card__image, .still-list-row__image');

    let productUrl = '';
    const linkEl = card.querySelector('a[href*="/products/"]');
    if (linkEl) {
      productUrl = linkEl.getAttribute('href') || '';
    } else if (card.dataset.productHandle) {
      productUrl = '/products/' + card.dataset.productHandle;
    }

    return {
      title: titleEl ? titleEl.textContent.trim() : (card.dataset.productHandle || ''),
      meta: [
        setEl ? setEl.textContent.trim() : '',
        numEl ? numEl.textContent.trim() : '',
      ].filter(Boolean).join('  '),
      imgSrc: imgEl ? imgEl.src : '',
      productUrl: productUrl,
    };
  }

  // ---------------------------------------------------------------------------
  // Modal builders
  // ---------------------------------------------------------------------------

  function buildVariantRow(v, cardTitle) {
    const oos = !v.available;
    let label = v.title || '';
    if (label === 'Default Title' && cardTitle) label = cardTitle;
    const qty = (typeof v.qty === 'number') ? v.qty : 0;
    // Cap stepper at available stock (or 99 if Shopify reports an
    // unbounded variant — rare for our SKU-tracked TCG inventory but
    // defensive). Min stays at 1; a 0-qty add doesn't make sense.
    const maxQty = qty > 0 ? qty : 99;

    let html = '<div class="still-qa-modal__row' + (oos ? ' still-qa-modal__row--oos' : '') + '">';
    html += '<span class="still-qa-modal__condition">' + escapeHtml(label);
    if (qty > 0 && !oos) {
      html += '<span class="still-qa-modal__stock">' + qty + ' avail</span>';
    }
    html += '</span>';
    html += '<span class="still-qa-modal__price">' + fmtPrice(v.price) + '</span>';
    if (oos) {
      html += '<span class="still-qa-modal__oos-label">OOS</span>';
    } else if (v.id != null) {
      // Qty stepper — minus / number-input / plus. Lets customers add
      // multiple copies in one click instead of clicking Add N times.
      // Number input is keyboard-friendly; ± buttons add mouse
      // discoverability. Both bounded by [1, maxQty].
      html += '<div class="still-qa-modal__stepper" data-still-qa-stepper>';
      html +=   '<button type="button" class="still-qa-modal__step-btn" data-still-qa-step="-1" aria-label="Decrease quantity">−</button>';
      html +=   '<input type="number" class="still-qa-modal__qty-input" '
            +     'value="1" min="1" max="' + maxQty + '" '
            +     'aria-label="Quantity" '
            +     'data-still-qa-qty>';
      html +=   '<button type="button" class="still-qa-modal__step-btn" data-still-qa-step="1" aria-label="Increase quantity">+</button>';
      html += '</div>';
      html += '<button type="button" class="still-qa-modal__add-btn" data-still-qa-add="' + escapeHtml(v.id) + '">Add</button>';
    }
    html += '</div>';
    return html;
  }

  function buildHeader(meta) {
    let html = '<div class="still-qa-modal__header">';
    if (meta.imgSrc) {
      html += '<div class="still-qa-modal__image"><img src="' + escapeHtml(meta.imgSrc) + '" alt="' + escapeHtml(meta.title) + '"></div>';
    }
    html += '<div class="still-qa-modal__info">';
    html += '<p class="still-qa-modal__title">' + escapeHtml(meta.title) + '</p>';
    if (meta.meta) html += '<p class="still-qa-modal__meta">' + escapeHtml(meta.meta) + '</p>';
    if (meta.productUrl) {
      html += '<a href="' + escapeHtml(meta.productUrl) + '" class="still-qa-modal__link">View Card &rarr;</a>';
    }
    html += '</div>';
    html += '<button type="button" class="still-qa-modal__close" data-still-qa-close aria-label="Close">&times;</button>';
    html += '</div>';
    return html;
  }

  function buildBody(groupData, cardTitle) {
    if (groupData.length === 0) {
      return '<div class="still-qa-modal__body"><p style="padding:20px;color:var(--color-still-muted, #999);">No variants available.</p></div>';
    }

    if (groupData.length === 1) {
      let body = '<div class="still-qa-modal__body">';
      groupData[0].variants.forEach(function (v) { body += buildVariantRow(v, cardTitle); });
      body += '</div>';
      return body;
    }

    // Multi-finish: tabs + panels.
    let tabs = '<div class="still-qa-modal__tabs">';
    let panels = '<div class="still-qa-modal__body">';
    groupData.forEach(function (vg, i) {
      const activeCls = i === 0 ? ' still-qa-modal__tab--active' : '';
      tabs += '<button type="button" class="still-qa-modal__tab' + activeCls + '" data-still-qa-tab="' + i + '">' + escapeHtml(vg.cardType) + '</button>';

      panels += '<div class="still-qa-modal__panel" data-still-qa-panel="' + i + '"' + (i === 0 ? '' : ' hidden') + '>';
      vg.variants.forEach(function (v) { panels += buildVariantRow(v, cardTitle); });
      panels += '</div>';
    });
    tabs += '</div>';
    panels += '</div>';
    return tabs + panels;
  }

  // Canonical tab order. Finishes not in this list fall to the end,
  // alphabetically. Keeps "Normal" first, then Holo, then Reverse Holo,
  // so the default-active tab is the one customers expect.
  const FINISH_ORDER = ['Normal', 'Holofoil', 'Reverse Holo'];

  function sortFinishes(a, b) {
    const ai = FINISH_ORDER.indexOf(a);
    const bi = FINISH_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  }

  /**
   * Merge variants that represent the same purchase option. Two variants
   * collapse into one row when title and price match — typical case: a
   * legacy Shopify product and a seeded placeholder for the same card
   * both have variant "Default Title" @ same ₱. Qty sums across both;
   * the surviving variant ID is any in-stock one (so `Add` POSTs a real
   * purchasable variant).
   */
  function mergeEquivalentVariants(variants) {
    const byKey = new Map();
    variants.forEach(function (v) {
      const key = (v.title || '') + '|' + String(v.price || 0);
      const existing = byKey.get(key);
      if (existing) {
        existing.qty = (Number(existing.qty) || 0) + (Number(v.qty) || 0);
        // Once any copy becomes available, the merged row is available.
        if (v.available) {
          existing.available = true;
          // Prefer an available variant id so Add-to-cart hits a real one.
          if (!existing._pickedAvailable) {
            existing.id = v.id;
            existing._pickedAvailable = true;
          }
        }
      } else {
        const copy = Object.assign({}, v);
        if (v.available) copy._pickedAvailable = true;
        byKey.set(key, copy);
      }
    });
    return Array.from(byKey.values()).map(function (v) {
      delete v._pickedAvailable;
      return v;
    });
  }

  function openModalForCard(card) {
    if (!card) return;
    ensureModal();

    const meta = getCardMeta(card);
    const siblings = findSiblings(card);

    // Step 1: collect variants grouped by finish. Multiple Shopify products
    // can share a finish (legacy + seeded placeholder duplicates); the
    // customer only wants to see one tab per finish, not one per product.
    const byFinish = {};
    siblings.forEach(function (sib) {
      const finish = finishFromHandle(sib.dataset.productHandle || '');
      const variants = parseVariants(sib);
      if (!variants.length) return;
      if (!byFinish[finish]) byFinish[finish] = [];
      byFinish[finish] = byFinish[finish].concat(variants);
    });

    // Step 2: merge equivalent variants within each finish, then order
    // finishes canonically (Normal → Holo → Reverse Holo → others), with
    // in-stock finishes promoted to front.
    let groupData = Object.keys(byFinish)
      .sort(sortFinishes)
      .map(function (finish) {
        return {
          cardType: finish,
          handle: '',
          variants: mergeEquivalentVariants(byFinish[finish]),
        };
      })
      .filter(function (g) { return g.variants.length > 0; });

    groupData.sort(function (a, b) {
      const aAvail = a.variants.some(function (v) { return v.available; }) ? 1 : 0;
      const bAvail = b.variants.some(function (v) { return v.available; }) ? 1 : 0;
      if (aAvail !== bAvail) return bAvail - aAvail;
      return sortFinishes(a.cardType, b.cardType);
    });

    modal.innerHTML = buildHeader(meta) + buildBody(groupData, meta.title);
    modal.classList.add('active');
    backdrop.classList.add('active');
    document.body.style.overflow = 'hidden';

    /* Live cart-aware stepper bound. Pre-fix the stepper max came
       only from the Liquid-rendered inventory_quantity, which doesn't
       know how many copies the customer has already added in this
       session. Fetch /cart.js, subtract per-variant qty in cart from
       each stepper's max, and clamp the input value too. Without
       this, customers could stage adds totaling more than stock
       and Shopify would silently reject the third one (or accept
       it and the order would fail at checkout). */
    if (typeof window.stillFetchCartQuantities === 'function') {
      window.stillFetchCartQuantities().then(function (cartByVariant) {
        const inputs = modal.querySelectorAll('[data-still-qa-qty]');
        inputs.forEach(function (input) {
          const row = input.closest('.still-qa-modal__row');
          if (!row) return;
          const addBtn = row.querySelector('[data-still-qa-add]');
          if (!addBtn) return;
          const variantId = addBtn.getAttribute('data-still-qa-add');
          if (!variantId) return;
          const inCart = cartByVariant.get(parseInt(variantId, 10)) || 0;
          if (inCart <= 0) return;
          const liquidMax = parseInt(input.getAttribute('max'), 10) || 99;
          const realMax = Math.max(0, liquidMax - inCart);
          input.setAttribute('max', String(realMax));
          /* Clamp current value too, in case prior adds bumped past it. */
          const cur = parseInt(input.value, 10) || 1;
          input.value = String(Math.min(realMax || 1, Math.max(1, cur)));

          /* Disable Add when no remaining stock to prevent the doomed
             POST. Show the cap inline so the customer knows why. */
          if (realMax <= 0) {
            addBtn.disabled = true;
            addBtn.textContent = 'In cart';
            input.disabled = true;
            const stockSpan = row.querySelector('.still-qa-modal__stock');
            if (stockSpan) stockSpan.textContent = inCart + ' in cart';
          } else {
            const stockSpan = row.querySelector('.still-qa-modal__stock');
            if (stockSpan) {
              stockSpan.textContent = realMax + ' avail · ' + inCart + ' in cart';
            }
          }
        });
      }).catch(function () { /* cart fetch is best-effort */ });
    }
  }

  // ---------------------------------------------------------------------------
  // Event delegation — one listener catches every relevant click on the page,
  // including buttons rendered by future AJAX re-renders.
  // ---------------------------------------------------------------------------

  document.addEventListener('click', function (e) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    // 1. +Cart trigger — open modal for the clicked card.
    const trigger = t.closest('.still-qa__trigger');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      openModalForCard(findCard(trigger));
      return;
    }

    // 2. Modal close button.
    if (t.closest('[data-still-qa-close]')) {
      e.preventDefault();
      closeModal();
      return;
    }

    // 3. Modal tab switch.
    const tab = t.closest('[data-still-qa-tab]');
    if (tab && modal && modal.contains(tab)) {
      e.preventDefault();
      const idx = tab.dataset.stillQaTab;
      modal.querySelectorAll('[data-still-qa-tab]').forEach(function (b) {
        b.classList.toggle('still-qa-modal__tab--active', b.dataset.stillQaTab === idx);
      });
      modal.querySelectorAll('[data-still-qa-panel]').forEach(function (p) {
        p.hidden = (p.dataset.stillQaPanel !== idx);
      });
      return;
    }

    // 4. Stepper ± buttons inside the modal — bump the sibling input.
    const stepBtn = t.closest('[data-still-qa-step]');
    if (stepBtn) {
      e.preventDefault();
      e.stopPropagation();
      const stepper = stepBtn.closest('[data-still-qa-stepper]');
      const input = stepper && stepper.querySelector('[data-still-qa-qty]');
      if (input) {
        const delta = parseInt(stepBtn.dataset.stillQaStep, 10) || 0;
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 99;
        const cur = parseInt(input.value, 10) || min;
        input.value = Math.min(max, Math.max(min, cur + delta));
      }
      return;
    }

    // 5. Add to cart button inside the modal — reads the row's
    //    stepper value (defaults to 1) so customers can grab multiple
    //    copies in one click instead of spamming Add N times.
    const addBtn = t.closest('[data-still-qa-add]');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      const variantId = addBtn.dataset.stillQaAdd;
      // The stepper sits as a sibling of the Add button in the same
      // row. Walk up to .still-qa-modal__row, then back down.
      const row = addBtn.closest('.still-qa-modal__row');
      const qtyInput = row && row.querySelector('[data-still-qa-qty]');
      let qty = 1;
      if (qtyInput) {
        const min = parseInt(qtyInput.min, 10) || 1;
        const max = parseInt(qtyInput.max, 10) || 99;
        qty = Math.min(max, Math.max(min, parseInt(qtyInput.value, 10) || 1));
      }
      if (typeof window.stillAddToCart === 'function' && variantId) {
        /* Wait for the add to resolve so we can decrement the stepper
           max in-place if successful. Without this, a customer who
           adds 2 in a session, sees the modal stay open, and tries
           to add 2 more would stage an oversell that Shopify silently
           rejects. */
        const result = window.stillAddToCart(variantId, addBtn, qty);
        if (result && typeof result.then === 'function') {
          result.then(function (r) {
            if (!r || !r.ok || !qtyInput) return;
            const remaining = (parseInt(qtyInput.getAttribute('max'), 10) || 99) - qty;
            qtyInput.setAttribute('max', String(Math.max(0, remaining)));
            qtyInput.value = String(Math.max(1, Math.min(remaining, parseInt(qtyInput.value, 10) || 1)));
            if (remaining <= 0) {
              addBtn.disabled = true;
              addBtn.textContent = 'In cart';
              qtyInput.disabled = true;
              const stockSpan = row && row.querySelector('.still-qa-modal__stock');
              if (stockSpan) stockSpan.textContent = qty + ' in cart';
            }
          });
        }
      }
      return;
    }
  });

  // ---------------------------------------------------------------------------
  // Card-link click interception (replaces the inline `stillCardClick` flow).
  // Capture phase so we run before the anchor's default navigation.
  // ---------------------------------------------------------------------------

  document.addEventListener('click', function (e) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    // Don't intercept the +Cart click — handler #1 above owns that.
    if (t.closest('.still-qa__trigger')) return;
    // Don't intercept clicks inside the modal itself.
    if (modal && modal.contains(t)) return;

    // Find the clickable card: either an <a class="still-card__link"> inside
    // a grid card, or the entire <a class="still-list-row"> in list view.
    const link = t.closest('a.still-card__link, a.still-list-row');
    if (!link) return;

    const card = findCard(link);
    if (!card) return;

    // Only intercept if there's actually a multi-variant group to show.
    // Single-variant cards navigate to the product page as normal.
    const siblings = findSiblings(card);
    if (siblings.length <= 1) return;

    e.preventDefault();
    e.stopPropagation();
    openModalForCard(card);
  }, true);

  // ---------------------------------------------------------------------------
  // Backwards-compat globals — let any inline onclick that still references
  // the old names continue to work.
  // ---------------------------------------------------------------------------

  window.stillQaOpenModal = function (e, btn) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    openModalForCard(findCard(btn));
  };

  window.stillQaCloseModal = closeModal;

  window.stillCardClick = function (e, link) {
    const card = findCard(link);
    if (!card) return true;
    const siblings = findSiblings(card);
    if (siblings.length <= 1) return true; // Allow navigation.
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    openModalForCard(card);
    return false;
  };

  // No-ops for the legacy names — the new event-delegated handlers replace them.
  window.stillQaModalTabSwitch = function () { /* delegated */ };
  window.stillQaModalAdd = function () { /* delegated */ };
})();
